import type {
  CreateQuestionRequest,
  QuestionContent,
  MediaIdsMap,
  SubQuestion,
} from 'src/types/helios/Question';
import { CreationMethod } from '@amzn/gbsa-helios-typescript-client';
import { DEFAULT_LANGUAGE } from 'src/contstant/commonLabels';
import type { ReviewQuestion } from './reviewTypes';

/** Identity + media inputs the pure mapper can't derive from the draft itself:
 *  the current user's program/alias, and the media already uploaded + assembled
 *  by `assembleQuestionMedia` (content rewritten to media-id srcs, the
 *  question-level map, and the per-sub media baked into each sub-question). */
export interface MapToCreateRequestInput {
  readonly programId: string;
  /** Alias stamped as `createdBy`. */
  readonly createdBy: string;
  /** Content with rich-text fields already rewritten to media-id srcs. */
  readonly content: QuestionContent;
  /** Question-level media map (criteria fields + task description). */
  readonly mediaIdsMap: MediaIdsMap;
  /** Sub-questions with their own `mediaIdsMap` already assembled. */
  readonly subQuestions: readonly SubQuestion[];
}

/** True when a map has at least one field with ids — used to omit an empty
 *  `mediaIdsMap` from the request rather than sending `{}`. */
function hasAnyMedia(map: MediaIdsMap): boolean {
  return Object.values(map).some((ids) => ids !== undefined && ids.length > 0);
}

/**
 * Maps a reviewed Astra draft to the Helios {@link CreateQuestionRequest} for
 * publishing. Pure: all media uploads/rewrites happen upstream in
 * `assembleQuestionMedia`; this only shapes the request.
 *
 * Most fields map 1:1 from `configuration`. The three values the draft can't
 * carry are supplied by the caller or fixed here:
 * - `programId` / `createdBy` — from the current user.
 * - `isActive: true` — publishing a reviewed question activates it.
 * - `defaultLanguage` — the app default (Astra drafts are authored in en-US).
 *
 * The source SOP rides in `docLinks` as an `isSourceSop: true` entry (seeded at
 * parse time), so passing `docLinks` through unchanged is what sends it — any
 * mapping here that rebuilt the links would drop the marker.
 */
export function mapReviewQuestionToCreateRequest(
  question: ReviewQuestion,
  input: MapToCreateRequestInput,
): CreateQuestionRequest {
  const { configuration: config } = question;
  const { programId, createdBy, content, mediaIdsMap, subQuestions } = input;

  const defaultLanguageContent: QuestionContent = {
    ...content,
    subQuestions: [...subQuestions],
  };

  return {
    programId,
    divisionName: config.divisionName,
    businessUnit: config.businessUnit,
    auditScore: config.auditScore,
    isActive: true,
    defaultLanguage: DEFAULT_LANGUAGE,
    defaultLanguageContent,
    categories: config.categories,
    docLinks: config.docLinks,
    tagsIncluded: config.tagsIncluded,
    ...(config.tagsExcluded ? { tagsExcluded: config.tagsExcluded } : {}),
    ...(hasAnyMedia(mediaIdsMap) ? { mediaIdsMap } : {}),
    locationsExcluded: config.locationsExcluded,
    subQuestions: [...subQuestions],
    requireCommentOnFail: config.requireCommentOnFail,
    requireEvidenceOnFail: config.requireEvidenceOnFail,
    requireCommentOnPass: config.requireCommentOnPass,
    requireEvidenceOnPass: config.requireEvidenceOnPass,
    ...(config.hasConditionalSubQuestions !== undefined
      ? { hasConditionalSubQuestions: config.hasConditionalSubQuestions }
      : {}),
    // Provenance: these questions come from the Astra generation flow.
    creationMethod: CreationMethod.ASTRA,
    createdBy,
  };
}
